import type {
  PracticePeriod,
  ReferralGraph,
  ReferralRelationshipAnalysis,
  SortOrder,
  UserDateCohort,
  UserDateField,
  UserSortField,
  UserStatisticsFilterState,
  UserWithActivity,
} from '@/types/user-statistics'

type RoleNameResolver = (role: UserWithActivity['role']) => string

const parseDateBoundary = (value: string, endOfDay: boolean): number | undefined => {
  if (!value) return undefined
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000'
  const timestamp = new Date(`${value}${suffix}`).getTime()
  return Number.isNaN(timestamp) ? undefined : timestamp
}

const getDateValue = (user: UserWithActivity, field: UserDateField): number | undefined => {
  const value = field === 'createdAt' ? user.createdAt : user.activityStats?.lastActiveTime
  if (!value) return undefined
  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? undefined : timestamp
}

const getSortValue = (user: UserWithActivity, field: UserSortField): number | undefined => {
  if (field === 'totalQuestions') return user.activityStats?.totalQuestions || 0
  if (field === 'wrongQuestionCount') return user.activityStats?.wrongQuestionCount || 0
  if (field === 'lastActiveTime') return getDateValue(user, 'lastActiveTime')
  return undefined
}

export const filterAndSortUsers = (
  users: UserWithActivity[],
  filters: UserStatisticsFilterState,
  getRoleName: RoleNameResolver,
): UserWithActivity[] => {
  const query = filters.searchQuery.trim().toLowerCase()
  const start = parseDateBoundary(filters.startDate, false)
  const end = parseDateBoundary(filters.endDate, true)

  if (start !== undefined && end !== undefined && start > end) return []

  const filtered = users.filter((user) => {
    if (query) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase()
      const matchesSearch = user.username.toLowerCase().includes(query)
        || fullName.includes(query)
        || Boolean(user.email?.toLowerCase().includes(query))
      if (!matchesSearch) return false
    }

    if (filters.roleFilter && getRoleName(user.role) !== filters.roleFilter) return false
    if (filters.statusFilter && user.isActive !== (filters.statusFilter === 'active')) return false

    if (start !== undefined || end !== undefined) {
      const dateValue = getDateValue(user, filters.dateField)
      if (dateValue === undefined) return false
      if (start !== undefined && dateValue < start) return false
      if (end !== undefined && dateValue > end) return false
    }

    return true
  })

  if (!filters.sortBy) return filtered

  const direction: Record<SortOrder, number> = { asc: 1, desc: -1 }
  return [...filtered].sort((left, right) => {
    const leftValue = getSortValue(left, filters.sortBy)
    const rightValue = getSortValue(right, filters.sortBy)

    if (leftValue === undefined && rightValue === undefined) return right.id - left.id
    if (leftValue === undefined) return 1
    if (rightValue === undefined) return -1

    const difference = leftValue - rightValue
    return difference === 0 ? right.id - left.id : difference * direction[filters.sortOrder]
  })
}

const localDateKey = (value: string | Date | null | undefined): string | undefined => {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export const buildUserDateCohorts = (
  users: UserWithActivity[],
  dateField: UserDateField,
): UserDateCohort[] => {
  const cohorts = new Map<string, UserDateCohort>()

  for (const user of users) {
    const value = dateField === 'createdAt' ? user.createdAt : user.activityStats?.lastActiveTime
    const date = localDateKey(value)
    if (!date) continue

    const cohort = cohorts.get(date) || { date, userCount: 0, totalQuestions: 0, wrongQuestionCount: 0 }
    cohort.userCount += 1
    cohort.totalQuestions += user.activityStats?.totalQuestions || 0
    cohort.wrongQuestionCount += user.activityStats?.wrongQuestionCount || 0
    cohorts.set(date, cohort)
  }

  return [...cohorts.values()].sort((left, right) => right.date.localeCompare(left.date))
}

export const summarizeUsers = (users: UserWithActivity[], getRoleName: RoleNameResolver) => {
  const roles = new Map<string, number>()
  let enabledUsers = 0
  let usersWithActivity = 0
  let totalQuestions = 0
  let wrongQuestionCount = 0

  for (const user of users) {
    const roleName = getRoleName(user.role) || 'unknown'
    roles.set(roleName, (roles.get(roleName) || 0) + 1)
    if (user.isActive) enabledUsers += 1
    if (user.activityStats?.lastActiveTime) usersWithActivity += 1
    totalQuestions += user.activityStats?.totalQuestions || 0
    wrongQuestionCount += user.activityStats?.wrongQuestionCount || 0
  }

  return {
    totalUsers: users.length,
    enabledUsers,
    disabledUsers: users.length - enabledUsers,
    usersWithActivity,
    totalQuestions,
    wrongQuestionCount,
    roles: [...roles.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name)),
  }
}

export const getPracticeQuestionCount = (user: UserWithActivity, period: PracticePeriod): number => {
  if (period === 'all') return user.activityStats?.totalQuestions || 0
  const periodTotals = user.activityStats?.practiceQuestionsByPeriod
  if (!periodTotals) return 0
  if (period === '1d') return periodTotals.lastDay
  if (period === '7d') return periodTotals.last7Days
  return periodTotals.last30Days
}

export const getTopPracticeUsers = (
  users: UserWithActivity[],
  limit = 10,
  period: PracticePeriod = 'all',
): UserWithActivity[] => (
  [...users]
    .sort((left, right) => (
      getPracticeQuestionCount(right, period) - getPracticeQuestionCount(left, period)
      || right.id - left.id
    ))
    .slice(0, limit)
)

export const getTopReferrers = (users: UserWithActivity[], limit = 10): UserWithActivity[] => (
  [...users]
    .filter(user => (user.referralStats?.invitedCount || 0) > 0)
    .sort((left, right) => (
      (right.referralStats?.invitedCount || 0) - (left.referralStats?.invitedCount || 0)
      || (right.referralStats?.qualifiedCount || 0) - (left.referralStats?.qualifiedCount || 0)
      || right.id - left.id
    ))
    .slice(0, limit)
)

export const buildReferralRelationships = (
  inviters: UserWithActivity[],
  allUsers: UserWithActivity[],
): ReferralRelationshipAnalysis[] => {
  const usersById = new Map(allUsers.map(user => [user.id, user]))
  return inviters
    .flatMap(inviter => (inviter.referralStats?.relationships || []).map(relationship => ({
      ...relationship,
      inviter,
      invitee: usersById.get(relationship.inviteeId),
    })))
    .sort((left, right) => {
      const leftTime = new Date(left.createdAt).getTime()
      const rightTime = new Date(right.createdAt).getTime()
      const normalizedLeftTime = Number.isNaN(leftTime) ? 0 : leftTime
      const normalizedRightTime = Number.isNaN(rightTime) ? 0 : rightTime
      return normalizedRightTime - normalizedLeftTime || right.id - left.id
    })
}

export const buildReferralGraph = (relationships: ReferralRelationshipAnalysis[]): ReferralGraph => {
  const nodes = new Map<number, ReferralGraph['nodes'][number]>()

  const ensureNode = (id: number, label: string) => {
    if (!nodes.has(id)) nodes.set(id, { id, label, incomingCount: 0, outgoingCount: 0 })
    return nodes.get(id)!
  }

  const edges = relationships.map((relationship) => {
    const inviter = ensureNode(relationship.inviterId, relationship.inviter.username || `用户 #${relationship.inviterId}`)
    const invitee = ensureNode(relationship.inviteeId, relationship.invitee?.username || `用户 #${relationship.inviteeId}`)
    inviter.outgoingCount += 1
    invitee.incomingCount += 1
    return {
      id: relationship.id,
      sourceId: relationship.inviterId,
      targetId: relationship.inviteeId,
      status: relationship.status,
    }
  })

  return {
    nodes: [...nodes.values()].sort((left, right) => (
      (right.outgoingCount + right.incomingCount) - (left.outgoingCount + left.incomingCount)
      || right.outgoingCount - left.outgoingCount
      || left.id - right.id
    )),
    edges,
  }
}
